import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound, CircleCheckBig, ShieldCheck, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Reviewer() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [activated, setActivated] = useState(false);
  const reviewerUser = activated ? user : null;

  const activate = async () => {
    if (!code.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post("/reviewer/activate", { code: code.trim() });
      setUser(res.data);
      setActivated(true);
      toast.success("Reviewer access activated");
    } catch (e) {
      toast.error("Invalid access code");
    } finally {
      setSubmitting(false);
    }
  };

  const formatExpiry = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    } catch (e) {
      return "";
    }
  };

  return (
    <div className="min-h-screen bg-decide-canvas">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-decide-canvas/85 border-b border-decide-line/70">
        <div className="decide-shell flex items-center justify-between h-14">
          <button onClick={() => navigate("/")} aria-label="Home" data-testid="reviewer-page-logo"><Logo size="sm" /></button>
        </div>
      </header>

      <main className="decide-shell py-8">
        {activated ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-10 flex flex-col items-center text-center" data-testid="reviewer-active">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-decide-forestLight">
              <CircleCheckBig size={38} className="text-decide-forest" />
            </div>
            <h1 className="mt-6 text-3xl font-light tracking-tight text-decide-ink">Reviewer access active</h1>
            <p className="mt-2 text-decide-slate">You have full DECIDE Pro access for review — no account or payment needed.</p>
            {reviewerUser?.reviewer_expires && (
              <p className="mt-1 text-sm text-decide-mute">Access valid until {formatExpiry(reviewerUser.reviewer_expires)}.</p>
            )}
            <button data-testid="reviewer-go-app" onClick={() => navigate("/app")} className="btn-accent mt-8">
              <Sparkles size={18} /> Start using DECIDE
            </button>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-decide-line bg-decide-surface">
              <KeyRound size={22} className="text-decide-forest" />
            </div>
            <h1 className="mt-5 text-3xl sm:text-4xl font-light tracking-tight text-decide-ink leading-tight">Reviewer access</h1>
            <p className="mt-4 text-decide-slate leading-relaxed">
              Enter your reviewer access code to unlock the complete DECIDE Pro experience for testing. No account or payment is required.
            </p>

            <div className="mt-4 rounded-2xl border border-decide-line bg-decide-surface p-4 flex items-start gap-3">
              <ShieldCheck size={18} className="mt-0.5 shrink-0 text-decide-forest" />
              <p className="text-[13px] leading-relaxed text-decide-slate">
                Access is validated securely on the server and expires automatically after 30 days. For authorized app reviewers only.
              </p>
            </div>

            <div className="mt-6">
              <label className="decide-label mb-2 block">Access code</label>
              <input
                data-testid="reviewer-code-input"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && activate()}
                placeholder="DECIDE-REVIEW-…"
                autoComplete="off"
                className="w-full rounded-2xl border border-decide-line bg-decide-surface px-4 py-3.5 text-decide-ink outline-none focus:border-decide-forest transition-colors"
              />
              <button
                data-testid="reviewer-activate-btn"
                disabled={submitting || !code.trim()}
                onClick={activate}
                className="btn-accent mt-4 w-full disabled:opacity-50"
              >
                <KeyRound size={17} /> {submitting ? "Activating…" : "Activate reviewer access"}
              </button>
            </div>

            <button onClick={() => navigate("/")} className="btn-ghost mt-4 w-full"><ArrowLeft size={16} /> Back to home</button>
          </motion.div>
        )}
      </main>
    </div>
  );
}
